import React from 'react';

import { IneligibleAnalytics } from '../analytics/audience-and-timing-analytics';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { useGetExperimentValue } from '../services/experiment-context';
import { isEligibleForAutomationFreeLimitModal } from '../services/is-eligible-for-automation-free-limit-modal';
import { isEligibleForConfluenceBuyReverseTrial } from '../services/is-eligible-for-confluence-buy-reverse-trial';
import { isEligibleForConfluencePremiumPopup } from '../services/is-eligible-for-confluence-premium-popup';
import { isEligibleForConfluenceStandardAddPaymentModal } from '../services/is-eligible-for-confluence-standard-add-payment-modal';
import { isEligibleForEAtoOSBFreeWithPDE } from '../services/is-eligible-for-ea-to-osb-free-with-pde';
import { isEligibleForEAtoOSBNonRTPremiumAndRTPredunning } from '../services/is-eligible-for-ea-to-osb-nonrt-premium-and-rt-predunning';
import { isEligibleForExploreEditionBenefitsModalReverseTrial } from '../services/is-eligible-for-explore-edition-benefits-modal-reverse-trial';
import { isEligibleForExploreEditionBenefitsModalStorageLimit } from '../services/is-eligible-for-explore-edition-benefits-modal-storage-limit';
import { isEligibleForExploreEditionBenefitsModalWithPDE } from '../services/is-eligible-for-explore-edition-benefits-modal-with-pde';
import { isEligibleForExploreEditionBenefitsModalWithoutPDE } from '../services/is-eligible-for-explore-edition-benefits-modal-without-pde';
import { isEligibleForFreeToStandardModal } from '../services/is-eligible-for-free-to-standard-modal';
import { isEligibleForJiraPremiumPredunning } from '../services/is-eligible-for-jira-premium-predunning';
import { isEligibleForJiraStandardPredunning } from '../services/is-eligible-for-jira-standard-predunning';
import { isEligibleForJsmStorageNearLimit } from '../services/is-eligible-for-jsm-storage-near-limit';
import { isEligibleForJsmStorageOverLimit } from '../services/is-eligible-for-jsm-storage-over-limit';
import { isEligibleForJWMExperiment } from '../services/is-eligible-for-jwm-experiment';
import { isEligibleForPremiumAddPaymentModal } from '../services/is-eligible-for-premium-add-payment-modal';
import { isEligibleForPremiumPaymentDetailsPopup } from '../services/is-eligible-for-premium-payment-details-popup';
import { isEligibleForReverseTrial } from '../services/is-eligible-for-reverse-trial';
import { isEligibleForReverseTrialPlanSelection } from '../services/is-eligible-for-reverse-trial-plan-selection';
import { isEligibleForStandardAddPaymentModal } from '../services/is-eligible-for-standard-add-payment-modal';
import { isEligibleForStandardPaymentDetailsPopup } from '../services/is-eligible-for-standard-payment-details-popup';
import { isEligibleForStandardPaymentFlowLink } from '../services/is-eligible-for-standard-payment-flow-link';
import { isEligibleForStandardToPremium } from '../services/is-eligible-for-standard-to-premium';
import { isEligibleForStorageHardLimit } from '../services/is-eligible-for-storage-hard-limit';
import { isEligibleForTWCAddPaymentDetails } from '../services/is-eligible-for-twc-add-payment-details';
import { isEligibleForTWCChangePlan } from '../services/is-eligible-for-twc-change-plan';
import { isEligibleForTWCPlanSelectionPage } from '../services/is-eligible-for-twc-plan-selection-page';
import { isEligibleForTWCTrialNoPDE } from '../services/is-eligible-for-twc-trial-no-pde';
import { isProductOneOf } from '../services/is-product-one-of';

import { AutomationFreeLimitModal } from './dialogs/automation-free-limit-modal';
import { CollectionAddPaymentDetailsPopup } from './dialogs/collection-add-payment-details-popup';
import { CollectionChangePlanPopup } from './dialogs/collection-change-plan-popup';
import { ExploreEditionBenefitsModal } from './dialogs/explore-edition-benefits-modal/explore-edition-benefits-modal';
import { FreeToStandardModal } from './dialogs/free-to-standard-modal';
import { PaymentDetailsPopup } from './dialogs/payment-details-popup';
import { PlanSelectionPageModal } from './dialogs/plan-selection-page-modal';
import { PremiumAddPaymentModal } from './dialogs/premium-add-payment-modal';
import { PremiumPopup } from './dialogs/premium-popup';
import { ReverseTrialPopupDialog } from './dialogs/reverse-trial-popup';
import { StandardAddPaymentModal } from './dialogs/standard-add-payment-modal';
import { StandardToPremiumModal } from './dialogs/standard-to-premium-modal';
import { StorageHardLimitModal } from './dialogs/storage-hard-limit-modal';
import { PaymentFlowLink } from './links/payment-flow-link';
import { UpgradeStandardFlowLink } from './links/upgrade-standard-flow-link';

function FreeEdition(): React.JSX.Element {
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();

	if (isEligibleForTWCPlanSelectionPage(context)) {
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForAutomationFreeLimitModal(context)) {
		return <AutomationFreeLimitModal />;
	}

	if (isEligibleForStorageHardLimit(context)) {
		return <StorageHardLimitModal />;
	}

	if (isEligibleForEAtoOSBFreeWithPDE(context)) {
		const cohort = getExperimentValue({
			statsigParams: {
				experimentName: 'ea-to-osb-free-with-pde-ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: false,
				},
			},
		});

		if (cohort === 'variation') {
			return <UpgradeStandardFlowLink />;
		}
	}

	if (
		isEligibleForJsmStorageOverLimit(context) ||
		isEligibleForJsmStorageNearLimit(context) ||
		isEligibleForFreeToStandardModal(context)
	) {
		return <FreeToStandardModal />;
	}

	return <IneligibleAnalytics />;
}

function StandardEdition(): React.JSX.Element {
	const context = useEditionAwareness();
	const {
		product,
		isInTrial,
		invoiceableStatus,
		canAddPaymentDetails,
		hasPaymentDetailsEntered,
		currentRoute,
	} = context;
	const { getExperimentValue } = useGetExperimentValue();

	if (isEligibleForTWCTrialNoPDE(context)) {
		// Teamwork Collection with no PDE in trial
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForTWCChangePlan(context)) {
		return <CollectionChangePlanPopup />;
	}

	if (isEligibleForTWCAddPaymentDetails(context)) {
		return <CollectionAddPaymentDetailsPopup />;
	}

	if (isEligibleForReverseTrialPlanSelection(context)) {
		return <PlanSelectionPageModal type="reverse-trial" />;
	}

	if (
		isInTrial &&
		isProductOneOf(product, ['jira-service-management', 'jira-customer-service']) &&
		(hasPaymentDetailsEntered ||
			invoiceableStatus === 'INVOICEABLE' ||
			(invoiceableStatus === 'NOT_INVOICEABLE' && canAddPaymentDetails))
	) {
		getExperimentValue({
			statsigParams: {
				experimentName: 'jsmgrowth-466_aa_test',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: true,
				},
			},
		});
	}

	// Storage-limit auto-upgrade path is independent of the STAR experiment / its
	// killswitch / cohort. Render the modal whenever the dedicated storage-limit
	// killswitch is on and the entitlement trial trigger is AUTO_UPGRADE_STORAGE_LIMIT.
	if (
		isEligibleForExploreEditionBenefitsModalStorageLimit(context) ||
		isEligibleForExploreEditionBenefitsModalReverseTrial(context)
	) {
		return <ExploreEditionBenefitsModal />;
	}

	if (isEligibleForExploreEditionBenefitsModalWithPDE(context)) {
		if (
			getExperimentValue({
				statsigParams: {
					experimentName: 'jsmgrowth466_star',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return <ExploreEditionBenefitsModal />;
		}
		return <IneligibleAnalytics />;
	}

	if (isEligibleForExploreEditionBenefitsModalWithoutPDE(context)) {
		if (
			getExperimentValue({
				statsigParams: {
					experimentName: 'jsmgrowth466_star',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return <ExploreEditionBenefitsModal />;
		}
		// jira-service-management intentionally falls through to the downstream isEligibleForStandardAddPaymentModal handler
	}

	// Catch-all: block CSM and ops routes that fell through (e.g. killswitch OFF, not in trial, or non-variation cohort)
	if (product === 'jira-customer-service' || currentRoute === 'ops') {
		return <IneligibleAnalytics />;
	}

	if (isEligibleForStandardPaymentFlowLink(context)) {
		return <PaymentFlowLink />;
	}

	if (isEligibleForStandardAddPaymentModal(context)) {
		return <StandardAddPaymentModal />;
	}

	// Jira Standard - Site admins get routed to JSTO, others to payment popup
	if (isEligibleForStandardPaymentDetailsPopup(context)) {
		return <PaymentDetailsPopup />;
	}

	if (isEligibleForConfluenceStandardAddPaymentModal(context)) {
		return <StandardAddPaymentModal />;
	}

	if (isEligibleForJiraStandardPredunning(context)) {
		return <PaymentFlowLink />;
	}

	// Keep at the bottom because is the only modal that is rendered for paid plans
	if (isEligibleForStandardToPremium(context)) {
		return <StandardToPremiumModal />;
	}

	return <IneligibleAnalytics />;
}

function PremiumEdition(): React.JSX.Element {
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();
	// make sure twc is always shown first before jira reverse trial
	// otherwise, jira reverse trial will be shown with collection

	// additional check on invoiceableStatus and canAddPaymentDetails
	// to block any EA to be shown if the customer is in a collection
	// * confluence premium is showing EA regardless which needs to be blocked from collection
	if (isEligibleForTWCTrialNoPDE(context)) {
		// Teamwork Collection with no PDE in trial
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForTWCChangePlan(context)) {
		return <CollectionChangePlanPopup />;
	}

	if (isEligibleForTWCAddPaymentDetails(context)) {
		return <CollectionAddPaymentDetailsPopup />;
	}

	if (isEligibleForEAtoOSBNonRTPremiumAndRTPredunning(context)) {
		const cohort = getExperimentValue({
			statsigParams: {
				experimentName: 'ea-to-osb-nonrt-premium-rt-predunning-ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: false,
				},
			},
		});

		if (cohort === 'variation') {
			return <PaymentFlowLink />;
		}
	}

	if (isEligibleForReverseTrialPlanSelection(context)) {
		return <PlanSelectionPageModal type="reverse-trial" />;
	}

	if (isEligibleForReverseTrial(context)) {
		return <ReverseTrialPopupDialog />;
	}

	if (isEligibleForPremiumPaymentDetailsPopup(context)) {
		return <PaymentDetailsPopup />;
	}

	if (isEligibleForJiraPremiumPredunning(context)) {
		return <PaymentFlowLink />;
	}

	if (isEligibleForConfluenceBuyReverseTrial(context)) {
		return <PlanSelectionPageModal type="reverse-trial" />;
	}

	if (isEligibleForConfluencePremiumPopup(context)) {
		return <PremiumPopup />;
	}

	if (isEligibleForPremiumAddPaymentModal(context)) {
		return <PremiumAddPaymentModal />;
	}

	return <IneligibleAnalytics />;
}

/**
 * Covers what button and action we show to the customer.
 *
 * Experiments here should just modify what is shown to the customer not if it is shown to the customer.
 */
export function ActionsAndContent(): React.JSX.Element | null {
	const context = useEditionAwareness();
	const { edition } = context;
	const { getExperimentValue } = useGetExperimentValue();

	if (
		isEligibleForJWMExperiment(context) &&
		getExperimentValue({
			statsigParams: {
				experimentName: 'twcg-1563-remove-jira-only-restriction',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
		}) !== 'variation'
	) {
		return <IneligibleAnalytics />;
	}

	if (edition === 'free') {
		return <FreeEdition />;
	}

	if (edition === 'standard') {
		return <StandardEdition />;
	}

	if (edition === 'premium') {
		return <PremiumEdition />;
	}

	return <IneligibleAnalytics />;
}
